import React, { useRef, useEffect } from "react";
import { Send, X } from "lucide-react";
import { formatMarkdown } from "@/utils/markdown";

export interface Message {
  id: string;
  content: string;
  isUser: boolean;
  isMarkdown?: boolean;
}

interface ChatBoxProps {
  messages: Message[];
  newMessage: string;
  onNewMessageChange: (message: string) => void;
  onSendMessage: () => void;
  onClose: () => void;
  isLoading?: boolean; // New prop for loading state
}

export const ChatBox: React.FC<ChatBoxProps> = ({
  messages,
  newMessage,
  onNewMessageChange,
  onSendMessage,
  onClose,
  isLoading,
}) => {
  // Reference to the messages container itself (not just the end)
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messagesContainerRef.current && messagesEndRef.current) {
      // Instead of scrolling the end element into view (which can affect page scroll)
      // directly manipulate the scroll position of the container
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSendMessage();
  };

  return (
    <div className="w-full max-w-[800px] bg-white bg-opacity-30 backdrop-blur-sm rounded-lg shadow-lg overflow-hidden mb-5 relative">
      <div className="flex justify-between items-center p-3 border-b border-white/20">
        <h3 className="text-[rgba(0,0,0,0.8)] font-medium">
          Anthony's personal assistant
        </h3>
        <button
          onClick={onClose}
          className="text-[rgba(0,0,0,0.6)] hover:text-[rgba(0,0,0,0.9)] transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      <div
        ref={messagesContainerRef}
        className="h-[400px] p-4 overflow-y-auto flex flex-col gap-3"
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className={`max-w-[80%] p-3 rounded-lg ${
              message.isUser
                ? "bg-[#28B88F] text-white self-end"
                : "bg-white bg-opacity-70 text-[rgba(0,0,0,0.8)] self-start"
            }`}
          >
            {message.isMarkdown ? (
              <div
                className="markdown-content"
                dangerouslySetInnerHTML={{
                  __html: formatMarkdown(message.content),
                }}
              />
            ) : (
              message.content
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
        {isLoading && (
          <div className="flex items-center p-3 animate-pulse">
            <div className="mr-2 text-gray-600">AI is typing</div>
            <div className="flex space-x-1">
              <div
                className="w-2 h-2 bg-gray-600 rounded-full animate-bounce"
                style={{ animationDelay: "0ms" }}
              ></div>
              <div
                className="w-2 h-2 bg-gray-600 rounded-full animate-bounce"
                style={{ animationDelay: "150ms" }}
              ></div>
              <div
                className="w-2 h-2 bg-gray-600 rounded-full animate-bounce"
                style={{ animationDelay: "300ms" }}
              ></div>
            </div>
          </div>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className="border-t border-white/20 p-3 flex gap-2"
      >
        <input
          type="text"
          value={newMessage}
          onChange={(e) => onNewMessageChange(e.target.value)}
          placeholder="Type your follow-up question..."
          className="flex-1 bg-white bg-opacity-50 rounded-full px-4 py-2 outline-none text-[rgba(0,0,0,0.7)]"
        />
        <button
          type="submit"
          className="bg-[#28B88F] text-white p-2 rounded-full hover:bg-[#1a9d76] transition-colors"
        >
          <Send size={20} />
        </button>
      </form>
    </div>
  );
};
